"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { EMI_SELECTED_VARIANT_EVENT } from "@/component/ui/CityPriceGrid/CityPriceGridReactive";
import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownList,
    Heading,
    HorizontalProductCard,
    Icon,
} from "@farm-junction/design-library";
import Image from "next/image";
import { customImageLoader } from "@/lib/utils/customLoader";
import LeadButton from "@/component/ui/Lead/LeadButton";
import MobileStickyCta, { MOBILE_STICKY_CTA_CLASS } from "@/component/pdp/MobileStickyCta";
import EmiCalculatorPageClient from "@/component/ui/EmiCalculator/EmiCalculatorPageClient";
import type { FetchBrandsResponse } from "@/app/utilities/interfaces";
import {
    buildEmiCalculatorDescription,
    formatEmiOnRoadRange,
} from "./emiCalculatorDescription";
import {
    calculateEMI,
    EMI_DEFAULT_INTEREST_RATE,
    EMI_DEFAULT_TENURE,
    formatEmiCurrency,
    getDefaultDownPayment,
} from "@/lib/utils/emi";
import { getOnRoadPrice } from "@/utils/onRoadPrice";
import type { CityData } from "@/types/city";

const BRAND_COLOR = "#008C8B";

type LiveEmiMetrics = {
    emi: number;
    loanMonths: number;
    effectivePrice: number;
    interestRate: number;
    downPayment: number;
    loanAmount: number;
};

type BrandOption = {
    _id: string;
    name: string;
    slug?: string;
    image?: string;
    is_popular?: boolean;
    is_active?: boolean;
};

type ModelOption = {
    _id: string;
    name: string;
    slug: string;
    detailed_slug?: string;
    brand_id?: string;
    brand_name?: string;
    brand_slug?: string;
    featured_image?: string | null;
    is_discontinued?: boolean;
};

type EmiCalculatorMobileViewProps = {
    className?: string;
    modelObj: any;
    initialBrand: BrandOption;
    initialModel: ModelOption;
    brands: FetchBrandsResponse["data"];
    variants: any[];
    selectedVariant: any;
    defaultLocation?: CityData | null;
    prefetchedReviewRating?: number;
    prefetchedReviewCount?: number;
    cdnUrl?: string;
    variantEmiTable?: ReactNode;
};

export default function EmiCalculatorMobileView({
    className,
    modelObj,
    initialBrand,
    initialModel,
    brands,
    variants,
    selectedVariant,
    defaultLocation = null,
    prefetchedReviewRating,
    prefetchedReviewCount,
    cdnUrl = process.env.NEXT_PUBLIC_CDN_URL || "",
    variantEmiTable,
}: EmiCalculatorMobileViewProps) {
    const descriptionRef = useRef<HTMLDivElement>(null);
    const offerBtnRef = useRef<HTMLDivElement>(null);
    const hiddenLeadBtnRef = useRef<HTMLDivElement>(null);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(true);
    const [activeVariant, setActiveVariant] = useState<any>(selectedVariant);
    const [cardVariantId, setCardVariantId] = useState<string | undefined>(selectedVariant?._id);
    const [isVariantDropdownOpen, setIsVariantDropdownOpen] = useState(false);
    const [liveEmiMetrics, setLiveEmiMetrics] = useState<LiveEmiMetrics>(() => {
        const variant =
            selectedVariant ||
            variants.find((v) => v?.is_base_variant) ||
            variants.find((v) => v?.status !== "discontinued");
        if (!variant) {
            return {
                emi: 0,
                loanMonths: EMI_DEFAULT_TENURE,
                effectivePrice: 0,
                interestRate: EMI_DEFAULT_INTEREST_RATE,
                downPayment: 0,
                loanAmount: 0,
            };
        }
        const onRoad = getOnRoadPrice(variant, modelObj?.locationInfo)?.onRoadPrice || Number(variant?.price) || 0;
        const downPayment = getDefaultDownPayment(onRoad);
        const { emi, loanAmount } = calculateEMI({
            price: onRoad,
            downPayment,
            interestRate: EMI_DEFAULT_INTEREST_RATE,
            tenureMonths: EMI_DEFAULT_TENURE,
        });
        return {
            emi,
            loanMonths: EMI_DEFAULT_TENURE,
            effectivePrice: onRoad,
            interestRate: EMI_DEFAULT_INTEREST_RATE,
            downPayment,
            loanAmount,
        };
    });

    useEffect(() => {
        setActiveVariant(selectedVariant);
        setCardVariantId(selectedVariant?._id);
    }, [selectedVariant]);

    useEffect(() => {
        const onVariantChange = (event: Event) => {
            const detail = (event as CustomEvent).detail;
            if (detail) setActiveVariant(detail);
        };
        window.addEventListener(EMI_SELECTED_VARIANT_EVENT, onVariantChange);
        return () => window.removeEventListener(EMI_SELECTED_VARIANT_EVENT, onVariantChange);
    }, []);

    const brandName = initialBrand?.name || modelObj?.brand_name || "";
    const modelName = initialModel?.name || modelObj?.name || "";
    const brandSlug = initialBrand?.slug || modelObj?.brand_slug || "";
    const modelSlug = initialModel?.slug || modelObj?.slug || "";
    const cityName = modelObj?.locationInfo?.name || defaultLocation?.name || "";
    const rating = Number(
        prefetchedReviewRating ?? modelObj?.user_rating ?? modelObj?.average_rating ?? 0,
    );
    const reviewCount = Number(prefetchedReviewCount ?? modelObj?.reviews_count ?? 0);
    const cardRatingLabel = rating > 0 ? rating.toFixed(1) : undefined;
    const isDiscontinued = modelObj?.status === "discontinued" || selectedVariant?.status === "discontinued";
    const isUpcoming = modelObj?.status === "upcoming";

    const rawProductImage = modelObj?.featured_image || modelObj?.image || "";
    const productImageSrc = rawProductImage
        ? customImageLoader({
              src: /^https?:\/\//i.test(String(rawProductImage))
                  ? String(rawProductImage)
                  : `${cdnUrl}${String(rawProductImage).replace(/^\/+/, "")}`,
              width: 280,
              quality: 70,
          })
        : "";

    const pageTitle = `${brandName} ${modelName} Car EMI Calculator`.trim();

    const description = useMemo(
        () =>
            buildEmiCalculatorDescription({
                model: modelObj,
                variants,
                cityName,
            }),
        [modelObj, variants, cityName],
    );

    const { intro, sections, compare } = description;
    const hasCollapsedExtras = sections.length > 0 || Boolean(compare) || Boolean(variantEmiTable);

    useLayoutEffect(() => {
        const el = descriptionRef.current;
        if (!el || isDescriptionExpanded) return;
        setIsDescriptionOverflowing(el.scrollHeight > el.clientHeight + 1 || hasCollapsedExtras);
    }, [intro, isDescriptionExpanded, hasCollapsedExtras]);

    const heroEmiStats = useMemo(() => {
        const variant =
            selectedVariant ||
            variants.find((v) => v?.is_base_variant) ||
            variants.find((v) => v?.status !== "discontinued");
        if (!variant) return null;
        const onRoad = getOnRoadPrice(variant, modelObj?.locationInfo)?.onRoadPrice || Number(variant?.price) || 0;
        const downPayment = getDefaultDownPayment(onRoad);
        const { emi } = calculateEMI({
            price: onRoad,
            downPayment,
            interestRate: EMI_DEFAULT_INTEREST_RATE,
            tenureMonths: EMI_DEFAULT_TENURE,
        });
        return { emi, onRoad };
    }, [selectedVariant, variants, modelObj?.locationInfo]);

    const onRoadRange = useMemo(() => formatEmiOnRoadRange(modelObj, variants), [modelObj, variants]);
    const selectableVariants = useMemo(
        () => variants.filter((item) => item?.is_sanity_done !== false && item?.status !== "discontinued"),
        [variants],
    );
    const cardVariant = useMemo(
        () => selectableVariants.find((item) => item._id === cardVariantId) ?? activeVariant ?? selectableVariants[0] ?? null,
        [activeVariant, cardVariantId, selectableVariants],
    );

    const leadCarLabel = [brandName, modelName, cardVariant?.name || activeVariant?.name].filter(Boolean).join(" ");

    const triggerLeadOffer = () => {
        hiddenLeadBtnRef.current?.querySelector("button")?.click();
    };

    const offerButton = (fullWidth = false, label?: string, className?: string) => (
        <LeadButton
            carId={modelObj?._id}
            variantId={cardVariant?._id || activeVariant?._id || modelObj?.variant_id}
            carName={leadCarLabel}
            // btnText={label || (isUpcoming ? "Notify Me" : `View ${OFFER_MONTH} Offers`)}
            btnText={label || (isUpcoming ? "Notify Me" : `View Diwali Offers`)}
            is_upcoming={isUpcoming}
            formHeading={
                isUpcoming ? (
                    <>
                        Get Launch Updates on{" "}
                        <span className="font-semibold" style={{ color: BRAND_COLOR }}>
                            {leadCarLabel}
                        </span>
                    </>
                ) : (
                    <>
                        Get EMI offers for{" "}
                        <span className="font-semibold" style={{ color: BRAND_COLOR }}>
                            {leadCarLabel}
                        </span>
                    </>
                )
            }
            className={
                className
                    ? className
                    : fullWidth
                    ? "flex h-12 w-full items-center justify-center rounded-[8px] border-0 bg-[#008C8B] text-[14px] font-medium leading-none text-white hover:bg-[#007A79]"
                    : "flex shrink-0 items-center justify-center rounded-[8px] border-0 bg-[#008C8B] px-3 py-3 text-[14px] font-medium leading-none text-white hover:bg-[#007A79]"
            }
            useSplashPopup
        />
    );

    return (
        <section data-brand="car-junction" className={className} data-testid="emi-calculator-mobile-view">
            <div className="mb-3 flex min-w-0 flex-col gap-2 overflow-visible">
                <Heading
                    variant="h1"
                    level={1}
                    color="black"
                    fontWeight="bold"
                    className="!mb-1 !text-[20px] !leading-[28px] !tracking-normal !text-[#101828] break-words"
                >
                    {pageTitle}
                </Heading>
                {(rating > 0 || reviewCount > 0) && (
                    <div className="flex items-center gap-2 shrink-0">
                        {rating > 0 && (
                            <p className="text-[13px] leading-none">
                                <span className="font-bold text-black">{rating.toFixed(1)}</span>
                                <span className="font-normal text-[#5F6B7A]"> /</span>
                                <span className="text-[11px] font-normal text-[#5F6B7A]"> 5</span>
                            </p>
                        )}
                        {rating > 0 && (
                            <Image
                                src="https://static-asset.tractorjunction.com/cj/yellow-star.svg"
                                alt="Rating"
                                title="Rating"
                                width={12}
                                height={12}
                                className="size-3"
                            />
                        )}
                        {reviewCount > 0 && (
                            <a href="#customer-reviews" className="text-[13px] font-normal text-[#155DFC] hover:underline">
                                {reviewCount} Reviews
                            </a>
                        )}
                    </div>
                )}
            </div>

            {intro && (
                <div className="mb-4 text-[15px] leading-5 text-[#6B7280]">
                    <div ref={descriptionRef} className={isDescriptionExpanded ? "" : "line-clamp-2 overflow-hidden"}>
                        <p>{intro}</p>
                    </div>
                    <div
                        className={`mt-3 space-y-3 ${isDescriptionExpanded ? "block" : "hidden"}`}
                        aria-hidden={!isDescriptionExpanded}
                    >
                        {sections.map((section) => (
                            <div key={section.title}>
                                <p className="font-semibold text-[#101828]">{section.title}</p>
                                <ul className="mt-1.5 list-disc space-y-1 pl-5">
                                    {section.items.map((item) => (
                                        <li key={item.label}>
                                            <strong className="font-semibold text-[#101828]">{item.label}:</strong> {item.text}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                        {compare && <p>{compare}</p>}
                        {variantEmiTable}
                    </div>
                    {(isDescriptionOverflowing || isDescriptionExpanded) && (
                        <button
                            type="button"
                            className="mt-2 block p-0 text-[13px] font-medium leading-5 text-[#007AFF] cursor-pointer"
                            onClick={() => setIsDescriptionExpanded((open) => !open)}
                        >
                            {isDescriptionExpanded ? "Read Less" : "Read More"}
                        </button>
                    )}
                </div>
            )}

            <div ref={offerBtnRef} className="mb-4">
                <HorizontalProductCard
                    variant="standard"
                    className="w-full max-w-none border-[#D8DEE8] [&>div:first-child>div:last-child]:!right-0 [&>div:first-child>div.absolute>p]:!text-lg md:[&>div:first-child>div.absolute>p]:!text-[22px] [&>div:first-child_.size-3]:fill-[#F5A623] [&>div:last-child_button]:h-12 [&>div:last-child_button]:rounded-[8px] [&>div:last-child_button]:border-0 [&>div:last-child_button]:bg-[#008C8B] [&>div:last-child_button]:text-[14px] [&>div:last-child_button]:font-medium [&>div:last-child_button]:text-white [&>div:last-child_button]:hover:bg-[#007A79]"
                    brandName={brandName}
                    modelName={modelName}
                    rating={cardRatingLabel}
                    imageSrc={productImageSrc || "/images/placeholder-car.svg"}
                    imageAlt={`${brandName} ${modelName}`}
                    headerSurfaceClassName="bg-[#F9F9F9]"
                    priceLabel={`On-road price · ${cityName || "India"}`}
                    priceValue={onRoadRange ? onRoadRange.replace(/\s*Lakh\*?/gi, "").trim() : "—"}
                    priceSuffix={onRoadRange ? "Lakh*" : undefined}
                    emiLabel="EMI starts at"
                    emiAmount={heroEmiStats ? `₹${formatEmiCurrency(heroEmiStats.emi)}` : undefined}
                    ctaLabel={isDiscontinued ? " " : "Check EMI Offers"}
                    onCtaClick={isDiscontinued ? undefined : triggerLeadOffer}
                    middleContent={
                        <>
                            {selectableVariants.length > 0 && cardVariant ? (
                                <div className="border-b border-[#E8ECF0] px-2 py-3">
                                    <Dropdown
                                        trigger={
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                className="h-11 w-full justify-between rounded-xl border border-[#E5E7EB] bg-white px-4 text-[14px] font-semibold text-[#101828] hover:bg-white"
                                            >
                                                <span className="truncate">{cardVariant.name}</span>
                                                <Icon name="downCaret" width={12} height={12} fill="#6B7280" />
                                            </Button>
                                        }
                                        align="left"
                                        sideOffset={4}
                                        className="w-full font-sans"
                                        matchTriggerWidth
                                        open={isVariantDropdownOpen}
                                        onOpenChange={setIsVariantDropdownOpen}
                                    >
                                        <DropdownList className="max-h-64 overflow-y-auto">
                                            {selectableVariants.map((variant) => (
                                                <DropdownItem
                                                    key={variant._id}
                                                    onClick={() => {
                                                        setCardVariantId(variant._id);
                                                        setIsVariantDropdownOpen(false);
                                                    }}
                                                >
                                                    {variant.name}
                                                </DropdownItem>
                                            ))}
                                        </DropdownList>
                                    </Dropdown>
                                </div>
                            ) : null}

                            <div className="flex items-start justify-between gap-3 border-b border-[#E8ECF0] px-4 py-3">
                                <span className="text-[13px] text-[#8B93A0]">On-road price</span>
                                <div className="text-right">
                                    <p className="text-[15px] font-bold text-[#101828]">
                                        {liveEmiMetrics.effectivePrice > 0
                                            ? `₹${formatEmiCurrency(liveEmiMetrics.effectivePrice)}`
                                            : "—"}
                                    </p>
                                    <p className="text-[11px] text-[#8B93A0]">{cityName || "India"}</p>
                                </div>
                            </div>

                            <div className="flex items-start justify-between gap-3 border-b border-[#E8ECF0] px-4 py-3">
                                <span className="text-[13px] text-[#8B93A0]">Loan amount</span>
                                <div className="text-right">
                                    <p className="text-[15px] font-bold text-[#101828]">
                                        {liveEmiMetrics.loanAmount > 0
                                            ? `₹${formatEmiCurrency(liveEmiMetrics.loanAmount)}`
                                            : "—"}
                                    </p>
                                    <p className="text-[11px] text-[#8B93A0]">On-road – Down payment</p>
                                </div>
                            </div>

                            <div className="flex items-start justify-between gap-3 border-b border-[#E8ECF0] px-4 py-3">
                                <span className="text-[13px] text-[#8B93A0]">Down payment</span>
                                <div className="text-right">
                                    <p className="text-[15px] font-bold text-[#101828]">
                                        {liveEmiMetrics.downPayment > 0
                                            ? `₹${formatEmiCurrency(liveEmiMetrics.downPayment)}`
                                            : "—"}
                                    </p>
                                    <p className="text-[11px] text-[#8B93A0]">On-road – Loan amount</p>
                                </div>
                            </div>

                            <div className="mx-2 mb-3 mt-3 flex items-center justify-between rounded-[10px] bg-[#EEFFF4] px-4 py-3">
                                <span className="text-[14px] font-bold text-[#101828]">Monthly EMI</span>
                                <div className="text-right">
                                    <p className="text-[20px] font-bold leading-6 text-[#008C8B]">
                                        {liveEmiMetrics.emi > 0 ? `₹${formatEmiCurrency(liveEmiMetrics.emi)}` : "—"}
                                    </p>
                                    <p className="text-[11px] text-[#8B93A0]">
                                        For {liveEmiMetrics.loanMonths} months @ {liveEmiMetrics.interestRate.toFixed(1)}%
                                    </p>
                                </div>
                            </div>
                        </>
                    }
                />
                <div ref={hiddenLeadBtnRef} className="sr-only" aria-hidden>
                    {!isDiscontinued ? offerButton(true, "Check EMI Offers") : null}
                </div>
            </div>

            <EmiCalculatorPageClient
                heading={`Calculate ${brandName} ${modelName} Car Loan EMI`.trim()}
                isMobile
                mobileRevampLayout
                brands={brands}
                initialBrand={initialBrand}
                initialModel={initialModel}
                initialVariants={variants}
                initialSelectedVariantId={selectedVariant?._id}
                isEmiRoute
                defaultLocation={defaultLocation}
                hideRevampOfferButton
                externalSelectedVariantId={cardVariantId}
                onLiveMetricsChange={setLiveEmiMetrics}
            />
            {!isDiscontinued && (
                <MobileStickyCta anchorRef={offerBtnRef}>
                    {offerButton(true, "Check EMI Offers", MOBILE_STICKY_CTA_CLASS)}
                </MobileStickyCta>
            )}
        </section>
    );
}
